// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `blocks` route body (PR-B, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `blocks` route projector: the document-level BLOCK-definitions table, PAGINATED / SORTED /
// FIELD-projected. A DOCUMENT-LEVEL inventory — NOT space-scoped (`isDocumentLevelRoute`). Registered
// via `registerRouteProjector('blocks', projectBlocks)`.
//
// referenceCount (OPT-IN, O(entities) scan): present ONLY when `?includeReferenceCount=true`. The body
// scans `snapshot.entities` for INSERTs whose `blockName === record.name` across all spaces. Because
// the count is GEOMETRY-derived, the route binds `geometryRevision` IN ADDITION to `tableRevision`
// ONLY when the flag is set (fullstack:964 — "Cursor биндит `tableRevision`; при
// `?includeReferenceCount=1` дополнительно биндит `geometryRevision`"). `sort=count` is valid ONLY
// when the flag is set (else `unknown-sort` — a count sort needs the count column). The COUNT-gating
// lives HERE (the seam's generic value-validator only checks set membership; the gate depends on
// another query param). `snapshotRevision` rides as the page ANCHOR only (INV-9).
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the shared helpers. depcruise cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import { isLayoutBlockName } from "@dxf-ai-editor/drawing-model";

import {
  QUERY_VALIDATION_CODES,
  type RouteProjector,
  type SceneSnapshotRef,
  type SnapshotBlockRef,
  type StateReadPage,
} from "../sceneSummaryProjector";
import {
  compareFacetsBy,
  paginate,
  parseKeyDirSort,
  projectFacetFields,
  resolveProjectionMode,
} from "./projectionHelpers";

/**
 * The LEAN CORE field set projected when `?fields` is ABSENT (byte economy). Every member is allowlisted.
 * `rawChildByType` is a DEFAULT field (alongside its scalar twin `rawChildCount`): the per-block
 * unsupported-child-type breakdown is a documented fidelity channel (fullstack:964 / ПРИОРИТЕТ #1), and it
 * is byte-cheap in practice (empty `[]` for the overwhelmingly common all-supported block).
 * `rawChildByTypeTruncated` RIDES IN THE DEFAULT alongside `rawChildByType` (PR-B @codex round-3, finding #5):
 * the contract pairs the ordered array with its `*Truncated` companion (the SAME cap/`*Truncated` semantics
 * as the summary `byType` counts, fullstack:938/964) — so whenever the DEFAULT projection exposes the capped
 * array, the truncation SIGNAL must ride with it, else a >200-distinct-type block returns a SHORTENED array
 * with no marker that it was cut (the agent reads a partial list as complete). It is an absent OPTIONAL
 * (omitted) for the overwhelmingly common under-cap block (`projectFacetFields` omits an absent optional —
 * it can never appear without the array it qualifies anyway), so its presence in the default set adds ZERO
 * bytes for a normal block and the flag precisely when (and only when) the array is truncated.
 */
const LEAN_CORE_FIELDS = [
  "name",
  "basePoint",
  "handle",
  "description",
  "xrefPathName",
  "xrefStatus",
  "isAnnotative",
  "derivedIsAnonymous",
  "rawChildCount",
  "rawChildByType",
  "rawChildByTypeTruncated",
] as const satisfies readonly (keyof SnapshotBlockRef)[];

/** The legal `key:dir` sort KEYS (`count` is GATED on `?includeReferenceCount` — see the body). */
const BLOCK_SORT_KEYS = ["name", "count"] as const;
type BlockSortKey = (typeof BLOCK_SORT_KEYS)[number];

/** A block record + the OPTIONAL derived `referenceCount` (present only when the flag is set). */
type BlockRow = SnapshotBlockRef & { readonly referenceCount?: number };

/**
 * Build the per-block `referenceCount` map: count INSERT entities whose `blockName` names each block,
 * across ALL spaces (an O(entities) scan run ONLY when `?includeReferenceCount=true`). A block with
 * no INSERTs counts 0 (its row still appears — the block table is the source).
 */
function buildReferenceCounts(snapshot: SceneSnapshotRef): ReadonlyMap<string, number> {
  const counts = new Map<string, number>();
  for (const e of snapshot.entities) {
    if (e.blockName !== undefined && e.blockName.length > 0) {
      counts.set(e.blockName, (counts.get(e.blockName) ?? 0) + 1);
    }
  }
  return counts;
}

/**
 * Extract a block row's value for a sort key: `name` is the RAW record name (string); `count` is the
 * derived `referenceCount` (a number when the flag is set, else undefined → reaches here ONLY when the
 * count gate passed, so it is defined). The seam validated membership; an unexpected key → undefined.
 */
function blockSortValue(row: BlockRow, key: BlockSortKey): string | number | undefined {
  switch (key) {
    case "name":
      return row.name;
    case "count":
      return row.referenceCount;
    default:
      return undefined;
  }
}

/**
 * The `blocks` route body. enumerate-table → (opt-in referenceCount) → gate `sort=count` → sort →
 * (fullField | field-project) → paginate. Binds `routeRevisions` to `{ tableRevision }` (the
 * BlockRecord is table-derived); ADDS `geometryRevision` ONLY when `?includeReferenceCount=true` (the
 * referenceCount is geometry-derived). `snapshotRevision` rides as the page ANCHOR only.
 * `resolvedSpaceId: null` (document-level). byteTruncated: false.
 */
export const projectBlocks: RouteProjector = ({ query, cursor, snapshot }) => {
  const includeReferenceCount = query.includeReferenceCount === true;

  // `sort=count` GATE: a count sort needs the count column → `unknown-sort` UNLESS the flag is set
  // (fullstack:964). The seam's value-validator only checked set membership (count:asc/desc ARE in the
  // allowlist); the flag-dependent gate lives here. Mirror the seam's reject-page shape.
  const sortIsCount = query.sort === "count:asc" || query.sort === "count:desc";
  if (sortIsCount && !includeReferenceCount) {
    return countSortWithoutFlagReject(snapshot);
  }

  // ENUMERATE the block table (document-level — `?scope` does NOT filter it). EXCLUDE the AutoCAD layout
  // pseudo-blocks (`*Model_Space` / `*Paper_Space` / `*Paper_Space<N>`) — the DXF BLOCK_RECORD table carries
  // them as system layout records, but the route contract is a definitions inventory of REAL drawn content.
  // The canonical `isLayoutBlockName` predicate matches ONLY those layout names; it does NOT match the real
  // anonymous blocks (`*D`/`*U`/`*X`/`*E` — dimension/hatch/xref/dynamic definitions), which are retained.
  // Filter FIRST so a layout record never consumes a page slot, inflates `total`, or rides into a sort/scan.
  const realBlocks = snapshot.blocks.filter((block) => !isLayoutBlockName(block.name));

  // Attach the derived referenceCount ONLY when requested (else the column is omitted — an absent optional,
  // NOT 0). The count map is keyed by name, so it is independent of the filter; the filtered list is what the
  // rows (and thus sort/paginate/total/referenceCount-column) are built from.
  const counts = includeReferenceCount ? buildReferenceCounts(snapshot) : null;
  const rows: BlockRow[] = realBlocks.map((block) =>
    counts === null ? block : { ...block, referenceCount: counts.get(block.name) ?? 0 },
  );

  // SORT (before pagination — cursor-determinism). A COPY; stable on a tie. No `?sort` → table order.
  // TIE-BREAK `name` ASC (fullstack:964 — `?sort=count` → DESC referenceCount → name ASC; applies to
  // EVERY sort so an equal-count run resolves to the documented `name` order, not the DXF table order,
  // keeping pagination deterministic across the tie).
  const parsedSort = parseKeyDirSort<BlockSortKey>(query.sort, BLOCK_SORT_KEYS);
  const ordered =
    parsedSort === null
      ? rows
      : [...rows].sort((a, b) =>
          compareFacetsBy(a, b, parsedSort.key, parsedSort.dir, blockSortValue, "name"),
        );

  // PROJECTION-MODE + LIMIT. `?fullField` forces limit=1 + projects only that field; else lean/`?fields`.
  const { fields, limit } = resolveProjectionMode(query, LEAN_CORE_FIELDS);

  // PAGINATION — the SHARED paginate primitive.
  const { slice, nextOffsetKey } = paginate(ordered, cursor, limit);

  // FIELD PROJECTION (INV-1). An OPTIONAL field absent on the row is omitted — so `referenceCount` is
  // emitted ONLY when the flag attached it (an absent optional otherwise), and `layoutHandle` only when
  // the block carries it.
  const items = slice.map((row) => projectFacetFields(row as unknown as Record<string, unknown>, fields));

  return {
    items,
    nextOffsetKey,
    // ROUTE-SPECIFIC binding ([L7] / fullstack:964): the BlockRecord is table-derived (tableRevision);
    // referenceCount (when requested) is geometry-derived → ADD geometryRevision ONLY then. Adding it
    // unconditionally would false-stale a non-count blocks continuation after an unrelated geometry edit.
    routeRevisions: includeReferenceCount
      ? {
          tableRevision: snapshot.revisions.tableRevision,
          geometryRevision: snapshot.revisions.geometryRevision,
        }
      : { tableRevision: snapshot.revisions.tableRevision },
    resolvedSpaceId: null, // document-level — not space-scoped.
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    total: ordered.length,
  };
};

/**
 * The `unknown-sort` reject page for `?sort=count` WITHOUT `?includeReferenceCount=true`. Mirrors the
 * seam's `notYetOrInvalidPage` shape (empty items, empty `routeRevisions` — a reject page binds no
 * route revisions, it is not a paginated continuation; the `snapshotRevision` anchor is still carried).
 */
function countSortWithoutFlagReject(snapshot: SceneSnapshotRef): StateReadPage {
  return {
    items: [],
    nextOffsetKey: null,
    routeRevisions: {}, // a reject page binds NO route revisions (companion accepts {}).
    resolvedSpaceId: null,
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR.
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    validationCode: QUERY_VALIDATION_CODES.unknownSort,
    total: 0,
  };
}
